import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sparkles, ArrowLeft, Shield, Handshake, Compass, Heart, CheckCircle2, HelpCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { testTranslations } from '../translations/test';
import { useSeo } from '../hooks/useSeo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = 'https://calendly.com/stefruiz04/appel-decouverte-gratuit';

const PROFILE_ICONS = {
  pilier: Shield,
  conciliateur: Handshake,
  independant: Compass,
  sensible: Heart
};

const PROFILE_COLORS = {
  pilier: 'from-teal-500 to-teal-600',
  conciliateur: 'from-amber-500 to-amber-600',
  independant: 'from-teal-500 to-pink-500',
  sensible: 'from-pink-500 to-pink-600'
};

export const DiscoveryTest = () => {
  const { language } = useLanguage();
  const t = testTranslations[language];

  const isFr = language === 'fr';
  useSeo(
    isFr ? "Test gratuit | Mieux se comprendre grâce à la numérologie" : "Comprendre qui l'on est - Stef Ruiz",
    isFr ? "Faites le test gratuit de Stef Ruiz pour mieux comprendre ce que vous vivez et découvrir comment la numérologie peut vous aider à avancer." : "A product of emergent.sh"
  );

  const [step, setStep] = useState('intro'); // intro | 0..4 | capture | result
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ firstName: '', email: '' });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const totalQuestions = t.questions.length;
  const PROFILE_ORDER = ['pilier', 'conciliateur', 'independant', 'sensible'];
  const QUESTION_WEIGHTS = { q1: 1, q2: 1.5, q3: 1, q4: 1.25, q5: 1.5 };

  const computeResult = () => {
    // Raw counts (kept as-is for the CRM) + weighted scores (used only to pick the profile)
    const scores = { pilier: 0, conciliateur: 0, independant: 0, sensible: 0 };
    const weighted = { pilier: 0, conciliateur: 0, independant: 0, sensible: 0 };
    Object.entries(answers).forEach(([qid, profile]) => {
      if (scores[profile] !== undefined) {
        scores[profile] += 1;
        weighted[profile] += (QUESTION_WEIGHTS[qid] ?? 1);
      }
    });

    // Primary criterion: highest weighted score
    const maxW = Math.max(...Object.values(weighted));
    let tied = PROFILE_ORDER.filter((k) => weighted[k] === maxW && maxW > 0);

    // Tie-break 1: the profile chosen at Q5 (most self-defining) wins, if tied
    if (tied.length > 1 && answers.q5 && tied.includes(answers.q5)) {
      tied = [answers.q5];
    }
    // Tie-break 2: otherwise the profile chosen at Q2 wins, if tied
    if (tied.length > 1 && answers.q2 && tied.includes(answers.q2)) {
      tied = [answers.q2];
    }
    // If still tied after both criteria → double profile (exceptional)
    return { topKeys: tied.length ? tied : [PROFILE_ORDER[0]], scores };
  };

  const handleSelect = (questionIndex, option) => {
    const q = t.questions[questionIndex];
    setAnswers((prev) => ({ ...prev, [q.id]: option.profile }));
    setTimeout(() => {
      if (questionIndex < totalQuestions - 1) {
        setStep(questionIndex + 1);
      } else {
        setStep('capture');
      }
    }, 250);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.firstName.trim().length < 1 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError(t.capture.errorEmail);
      return;
    }
    if (!consent) {
      setError(t.capture.consentError);
      return;
    }
    const { topKeys, scores } = computeResult();
    const primary = t.profiles[topKeys[0]];
    const profileName = topKeys.map((k) => t.profiles[k].name).join(' / ');
    setSubmitting(true);
    try {
      await axios.post(`${API}/tests`, {
        firstName: form.firstName.trim(),
        email: form.email.trim(),
        profile: topKeys[0],
        profileName: profileName,
        profileDescription: primary.description,
        forces: primary.forces,
        defis: primary.defis,
        question: primary.question,
        answers: Object.entries(answers).map(([q, pr]) => ({ question: q, profile: pr })),
        scores,
        language,
        consentGiven: true
      });
      setResult({ topKeys });
      setStep('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error submitting test:', err);
      setError(t.capture.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const resetTest = () => {
    setAnswers({});
    setForm({ firstName: '', email: '' });
    setConsent(false);
    setResult(null);
    setError('');
    setStep('intro');
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-teal-50 via-white to-pink-50">
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">

          {/* INTRO */}
          {step === 'intro' && (
            <div className="text-center" data-testid="test-intro">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-100 text-teal-700 text-sm font-medium mb-6">
                <Sparkles size={16} />
                {t.intro.badge}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-teal-600 mb-4 leading-tight">
                {t.intro.title}
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed mb-3 font-light">
                {t.intro.subtitle}
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                {t.intro.description}
              </p>
              <Card className="border-none shadow-lg bg-white mb-8 text-left">
                <CardContent className="p-6 space-y-3">
                  {t.intro.points.map((point, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 size={20} className="text-teal-500 flex-shrink-0" />
                      <span className="text-gray-700">{point}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Button
                size="lg"
                data-testid="test-start-button"
                onClick={() => setStep(0)}
                className="bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 text-white text-lg px-10 py-6 h-auto shadow-xl transition-all duration-300 hover:scale-105"
              >
                {t.intro.startButton}
              </Button>
            </div>
          )}

          {/* QUESTIONS */}
          {typeof step === 'number' && (
            <div data-testid={`test-question-${step}`}>
              {/* Progress */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-teal-600">
                    {t.progress} {step + 1} {t.of} {totalQuestions}
                  </span>
                  <span className="text-sm text-gray-400">{Math.round(((step + 1) / totalQuestions) * 100)}%</span>
                </div>
                <div className="h-2 w-full bg-teal-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-teal-500 to-pink-500 rounded-full transition-all duration-500"
                    style={{ width: `${((step + 1) / totalQuestions) * 100}%` }}
                  />
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-light text-gray-900 mb-8 leading-snug">
                {t.questions[step].text}
              </h2>

              <div className="space-y-3">
                {t.questions[step].options.map((option) => {
                  const selected = answers[t.questions[step].id] === option.profile;
                  return (
                    <button
                      key={option.key}
                      data-testid={`test-option-${step}-${option.key}`}
                      onClick={() => handleSelect(step, option)}
                      className={`w-full text-left p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 flex items-center gap-4 ${
                        selected
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 bg-white hover:border-teal-300 hover:bg-teal-50/40'
                      }`}
                    >
                      <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-medium text-sm ${
                        selected ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {option.key}
                      </span>
                      <span className="text-gray-800 leading-relaxed">{option.text}</span>
                    </button>
                  );
                })}
              </div>

              {step > 0 && (
                <button
                  data-testid="test-back-button"
                  onClick={() => setStep(step - 1)}
                  className="mt-8 inline-flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
                >
                  <ArrowLeft size={18} />
                  {t.back}
                </button>
              )}
            </div>
          )}

          {/* CAPTURE */}
          {step === 'capture' && (() => {
            return (
            <Card className="border-none shadow-2xl" data-testid="test-capture">
              <CardContent className="p-8 sm:p-10">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-500 to-pink-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Sparkles size={32} className="text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-light text-teal-600 mb-4 uppercase tracking-wide" data-testid="test-capture-title">{t.capture.teaserReady} 🎉</h2>
                  <div className="bg-gradient-to-br from-teal-50 to-pink-50 rounded-xl px-5 py-4 border border-teal-100 text-left" data-testid="test-capture-teaser">
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">{t.capture.teaserIntro}</p>
                    <div className="space-y-1.5">
                      {t.capture.lockedItems.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-gray-400"><span className="text-base">🔒</span> {item}</div>
                      ))}
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">{t.capture.firstName}</Label>
                    <Input
                      id="firstName"
                      data-testid="test-firstname-input"
                      value={form.firstName}
                      onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
                      placeholder={t.capture.firstNamePlaceholder}
                      required
                      className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{t.capture.email}</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="test-email-input"
                      value={form.email}
                      onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                      placeholder={t.capture.emailPlaceholder}
                      required
                      className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="test-consent-checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 flex-shrink-0"
                    />
                    <span className="text-xs text-gray-600 leading-relaxed">
                      {t.capture.consentBefore}
                      <Link to="/confidentialite" target="_blank" className="text-teal-600 underline hover:text-teal-700">
                        {t.capture.consentLink}
                      </Link>
                      {t.capture.consentAfter}
                    </span>
                  </label>

                  {error && (
                    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-testid="test-error">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    data-testid="test-submit-button"
                    className="w-full bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 text-white text-lg py-6 h-auto disabled:opacity-70"
                  >
                    {submitting ? t.capture.submitting : t.capture.submitButton}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">{t.capture.privacy}</p>
                </form>
              </CardContent>
            </Card>
            );
          })()}

          {/* RESULT */}
          {step === 'result' && result && (() => {
            const topKeys = result.topKeys || [];
            const isTie = topKeys.length > 1;
            const tieNames = topKeys.map((k) => t.profiles[k].name).join(' & ');
            return (
              <div className="text-center" data-testid="test-result">
                <p className="text-gray-600 mb-2">{t.result.greeting} {form.firstName} 🌿</p>

                {isTie ? (
                  <>
                    <p className="text-base text-gray-500 mb-2">{t.result.tieLead}</p>
                    <h2 className="text-2xl sm:text-3xl font-light text-teal-600 mb-6" data-testid="test-result-tie-names">{tieNames}</h2>
                  </>
                ) : (
                  <p className="text-base text-gray-500 mb-6">{t.result.yourProfile}</p>
                )}

                {/* Tie explanation */}
                {isTie && (
                  <div className="bg-gradient-to-br from-teal-50 to-pink-50 rounded-2xl p-6 sm:p-8 mb-6 border border-teal-100 text-left" data-testid="test-result-tie">
                    <p className="text-gray-700 leading-relaxed">{t.result.tieText}</p>
                  </div>
                )}

                {/* Profile card(s) */}
                {topKeys.map((key) => {
                  const profile = t.profiles[key];
                  const Icon = PROFILE_ICONS[key];
                  const color = PROFILE_COLORS[key];
                  return (
                    <div key={key} className="mb-6">
                      <Card className="border-none shadow-2xl bg-white mb-4">
                        <CardContent className="p-8 sm:p-10">
                          <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${color} flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                            <Icon size={36} className="text-white" />
                          </div>
                          <h1 className="text-3xl sm:text-4xl font-light text-teal-600 mb-5" data-testid="test-result-profile">
                            {profile.name}
                          </h1>
                          <p className="text-base sm:text-lg text-gray-700 leading-relaxed text-left">
                            {profile.description}
                          </p>
                        </CardContent>
                      </Card>

                      {/* Question à explorer */}
                      <div className="bg-gradient-to-br from-amber-50 to-pink-50 rounded-2xl p-6 sm:p-8 border border-amber-100">
                        <div className="flex items-center justify-center gap-2 mb-3">
                          <HelpCircle size={20} className="text-amber-500" />
                          <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-600">{t.result.questionTitle}</h3>
                        </div>
                        <p className="text-lg sm:text-xl font-light text-gray-800 leading-relaxed italic">
                          {profile.question}
                        </p>
                      </div>
                    </div>
                  );
                })}

                {/* Closing text */}
                <div className="bg-gradient-to-br from-teal-50 to-pink-50 rounded-2xl p-6 sm:p-8 mb-8 border border-teal-100">
                  <p className="text-gray-700 leading-relaxed text-left">
                    {t.result.closing}
                  </p>
                </div>

                {/* Calendly CTA */}
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    data-testid="test-cta-button"
                    className="bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 text-white text-lg px-10 py-6 h-auto shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    📅 {t.result.ctaButton}
                  </Button>
                </a>
                <p className="text-sm text-gray-500 mt-3">{t.result.ctaSub}</p>

                <div>
                  <button
                    onClick={resetTest}
                    data-testid="test-retake-button"
                    className="mt-8 text-sm text-gray-500 hover:text-teal-600 transition-colors"
                  >
                    {t.result.retake}
                  </button>
                </div>
              </div>
            );
          })()}

        </div>
      </section>
    </div>
  );
};
